/**
 * Tasks Page
 * 
 * Centralized task management showing all tasks across the application
 * Supports Board, List, and Calendar views
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCw, AlertCircle, Clock, Calendar, CheckCircle2, ListTodo, LayoutGrid, CalendarDays, User, AlertTriangle, Zap } from 'lucide-react';
import { useTaskBoard } from '@/hooks/useTaskBoard';
import { KanbanBoard } from '@/components/task-board/kanban-board';
import { TaskFiltersComponent } from '@/components/task-board/task-filters';
import { TaskListView } from '@/components/task-board/task-list-view';
import { TaskCalendarView } from '@/components/task-board/task-calendar-view';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

type ViewMode = 'board' | 'list' | 'calendar';

type QuickFilter = 'my-tasks' | 'overdue' | 'due-today' | 'due-this-week' | 'blocked' | 'urgent';

export default function TaskBoardPage() {
  const { 
    stats, 
    columns, 
    filters, 
    setFilters, 
    clearFilters, 
    handleTaskStatusChange, 
    refreshTasks,
    allTasks,
  } = useTaskBoard();

  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [activeQuickFilters, setActiveQuickFilters] = useState<Set<QuickFilter>>(new Set());

  // Toggle quick filter
  const toggleQuickFilter = (filter: QuickFilter) => {
    const newFilters = new Set(activeQuickFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveQuickFilters(newFilters);

    // Apply filter logic
    applyQuickFilters(newFilters);
  };

  // Apply quick filters to the task board
  const applyQuickFilters = (quickFilters: Set<QuickFilter>) => {
    const newFilters = { ...filters };

    // Reset filters if no quick filters are active
    if (quickFilters.size === 0) {
      clearFilters();
      return;
    }

    // My Tasks - filter by current user (for now, we'll use a placeholder)
    if (quickFilters.has('my-tasks')) {
      // In a real app, this would filter by the logged-in user
      // For now, we'll just keep all tasks
    }

    // Overdue - tasks past their due date
    if (quickFilters.has('overdue')) {
      // This is handled by the stats calculation in useTaskBoard
      // We can add a custom filter here if needed
    }

    // Due Today - tasks due today
    if (quickFilters.has('due-today')) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      newFilters.dateRange = { start: today, end: tomorrow };
    }

    // Due This Week - tasks due within 7 days
    if (quickFilters.has('due-this-week')) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      newFilters.dateRange = { start: today, end: nextWeek };
    }

    // Blocked - tasks with blocked status
    if (quickFilters.has('blocked')) {
      newFilters.status = ['blocked'];
    }

    // Urgent - high priority tasks
    if (quickFilters.has('urgent')) {
      newFilters.priority = ['Critical', 'High'];
    }

    setFilters(newFilters);
  };

  return (
    <div className="flex-1 space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Tasks</h1>
            <p className="text-muted-foreground mt-1">
              Manage and track tasks across your organisation
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Switcher */}
            <div className="flex items-center border rounded-lg p-1 bg-muted/50">
              <Button
                variant={viewMode === 'board' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('board')}
                className="h-8"
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Board View
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8"
              >
                <ListTodo className="h-4 w-4 mr-2" />
                List View
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className="h-8"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Calendar View
              </Button>
            </div>
            <Button onClick={refreshTasks} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground">Quick Filters:</span>
          <Badge
            variant={activeQuickFilters.has('my-tasks') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('my-tasks') && "bg-primary text-primary-foreground"
            )}
            onClick={() => toggleQuickFilter('my-tasks')}
          >
            <User className="h-3 w-3 mr-1" />
            My Tasks
          </Badge>
          <Badge
            variant={activeQuickFilters.has('overdue') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('overdue') && "bg-red-600 text-white hover:bg-red-700"
            )}
            onClick={() => toggleQuickFilter('overdue')}
          >
            <AlertCircle className="h-3 w-3 mr-1" />
            Overdue
          </Badge>
          <Badge
            variant={activeQuickFilters.has('due-today') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('due-today') && "bg-orange-600 text-white hover:bg-orange-700"
            )}
            onClick={() => toggleQuickFilter('due-today')}
          >
            <Clock className="h-3 w-3 mr-1" />
            Due Today
          </Badge>
          <Badge
            variant={activeQuickFilters.has('due-this-week') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('due-this-week') && "bg-blue-600 text-white hover:bg-blue-700"
            )}
            onClick={() => toggleQuickFilter('due-this-week')}
          >
            <Calendar className="h-3 w-3 mr-1" />
            Due This Week
          </Badge>
          <Badge
            variant={activeQuickFilters.has('blocked') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('blocked') && "bg-gray-600 text-white hover:bg-gray-700"
            )}
            onClick={() => toggleQuickFilter('blocked')}
          >
            <AlertTriangle className="h-3 w-3 mr-1" />
            Blocked
          </Badge>
          <Badge
            variant={activeQuickFilters.has('urgent') ? 'default' : 'outline'}
            className={cn(
              "cursor-pointer transition-colors",
              activeQuickFilters.has('urgent') && "bg-purple-600 text-white hover:bg-purple-700"
            )}
            onClick={() => toggleQuickFilter('urgent')}
          >
            <Zap className="h-3 w-3 mr-1" />
            Urgent
          </Badge>
        </div>

        {/* Advanced Filters */}
        <Card>
          <CardContent className="p-4">
            <TaskFiltersComponent
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={() => {
                clearFilters();
                setActiveQuickFilters(new Set());
              }}
            />
          </CardContent>
        </Card>

        {/* Task Views */}
        {stats.total === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ListTodo className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Tasks Found</h3>
              <p className="text-muted-foreground mb-4">
                You don't have any active tasks at the moment. Tasks will appear here from:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 max-w-md mx-auto">
                <li>• Active collection cases requiring follow-up</li>
                <li>• Promises to pay (active and broken)</li>
                <li>• Disputes requiring response</li>
                <li>• Unallocated receipts needing allocation</li>
              </ul>
            </CardContent>
          </Card>
        ) : (
          <>
            {viewMode === 'board' && (
              <KanbanBoard
                columns={columns}
                onTaskStatusChange={handleTaskStatusChange}
              />
            )}
            {viewMode === 'list' && (
              <TaskListView
                tasks={allTasks}
                onTaskStatusChange={handleTaskStatusChange}
              />
            )}
            {viewMode === 'calendar' && (
              <TaskCalendarView
                tasks={allTasks}
                onTaskStatusChange={handleTaskStatusChange}
              />
            )}
          </>
        )}
    </div>
  );
}
